import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';
import { Instagram, Facebook, Twitter, Mail, Phone, MapPin } from 'lucide-react';
import CookieManager from './CookieManager';
import { settingsAPI, publicAPI } from '../services/apiService';

const Footer = () => {
  const { t } = useTranslation();
  const [showCookieManager, setShowCookieManager] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    instagram: '',
    facebook: '',
    linkedin: '',
    whatsapp: ''
  });
  const [contactInfo, setContactInfo] = useState({
    contact_email: 'info@fotografx.de',
    contact_phone: '+49 (0) 123 456 789',
    contact_address: 'Berlin, Deutschland',
    contact_hours: 'Mo-Fr 09:00-18:00, Sa 10:00-16:00'
  });
  const [siteBranding, setSiteBranding] = useState({
    site_name: 'FotografX',
    site_description_de: 'Professionelle Fotografie für Ihre besonderen Momente',
    site_description_ru: 'Профессиональная фотография для ваших особенных моментов'
  });

  useEffect(() => {
    // Delay the social links fetch to avoid blocking page render
    const timer = setTimeout(() => {
      fetchSocialLinks();
    }, 1000);
    
    fetchContactInfo();
    fetchSiteBranding();
    
    return () => clearTimeout(timer);
  }, []);

  const fetchSiteBranding = async () => {
    try {
      const response = await publicAPI.getSiteBranding();
      setSiteBranding(response.data);
    } catch (error) {
      console.error('Error fetching site branding:', error);
    }
  };

  const fetchContactInfo = async () => {
    try {
      const response = await publicAPI.getContactSettings();
      setContactInfo(response.data);
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      // Try to fetch social links from settings API
      const response = await settingsAPI.getUserSettings();
      if (response.data.social_links) {
        setSocialLinks(response.data.social_links);
      }
    } catch (error) {
      console.error('Error fetching social links:', error);
      // Use default/fallback links if API fails - this is expected for public users
      // Set empty social links to prevent further API calls
      setSocialLinks({
        instagram: '',
        facebook: '', 
        linkedin: ''
      });
    }
  };

  return (
    <footer className="bg-black border-t border-gray-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand and Description */}
          <div className="space-y-4">
            <h3 
              className="text-2xl font-bold text-white"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {siteBranding.site_name.toUpperCase()}
            </h3>
            <p className="text-gray-400 text-sm leading-relaxed">
              {i18n.language === 'ru' ? siteBranding.site_description_ru : siteBranding.site_description_de}
            </p>
            <div className="flex space-x-4">
              {socialLinks.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Instagram className="h-5 w-5" />
                </a>
              )}
              {socialLinks.facebook && (
                <a 
                  href={socialLinks.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Facebook className="h-5 w-5" />
                </a>
              )}
              {socialLinks.linkedin && (
                <a 
                  href={socialLinks.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200"
                >
                  <Twitter className="h-5 w-5" />
                </a>
              )}
              {/* Show default links if no social links are configured */}
              {!socialLinks.instagram && !socialLinks.facebook && !socialLinks.linkedin && (
                <>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200">
                    <Twitter className="h-5 w-5" />
                  </a>
                </>
              )}
            </div>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h4 
              className="text-lg font-semibold text-white"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('contact.title')}
            </h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 text-gray-400">
                <Mail className="h-4 w-4" />
                <span className="text-sm">{contactInfo.contact_email}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400">
                <Phone className="h-4 w-4" />
                <span className="text-sm">{contactInfo.contact_phone}</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-400">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">{contactInfo.contact_address}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 
              className="text-lg font-semibold text-white"
              style={{ fontFamily: '"Times New Roman", serif' }}
            >
              {t('footer.quickLinks')}
            </h4>
            <div className="space-y-2">
              <a href="/portfolio" className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('nav.portfolio')}
              </a>
              <a href="/services" className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('nav.services')}
              </a>
              <a href="/contact" className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('nav.contact')}
              </a>
              <a href="/booking" className="block text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('nav.booking')}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 {siteBranding.site_name}. {t('footer.rights')}.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/impressum" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('footer.impressum')}
              </Link>
              <Link to="/datenschutz" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('footer.privacy')}
              </Link>
              <Link to="/agb" className="text-gray-400 hover:text-white transition-colors duration-200 text-sm">
                {t('footer.terms')}
              </Link>
              <button
                onClick={() => setShowCookieManager(true)}
                className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
              >
                {t('footer.cookieSettings')}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <CookieManager 
        isOpen={showCookieManager} 
        onClose={() => setShowCookieManager(false)} 
      />
    </footer>
  );
};

export default Footer;